import React from 'react';
import { Button, Card, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb, faClock, faGavel, faFileContract, faHome, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

interface TimingTemplate {
  name: string;
  icon: any;
  trigger: string;
  deadline: string;
  priority: string;
  description: string;
  compliance?: string;
}

const TIMING_TEMPLATES: TimingTemplate[] = [
  {
    name: 'Earnest Money Deposit',
    icon: faGavel,
    trigger: 'upon_contract_acceptance',
    deadline: 'within_2_business_days',
    priority: 'critical',
    description: 'Funds to secure the purchase agreement',
    compliance: 'State law requirement'
  },
  {
    name: 'Inspection Period',
    icon: faHome,
    trigger: 'upon_mutual_acceptance',
    deadline: 'within_10_days',
    priority: 'high',
    description: 'Buyer\'s right to inspect property',
    compliance: 'Contract contingency'
  },
  {
    name: 'Loan Application',
    icon: faFileContract,
    trigger: 'upon_contract_acceptance',
    deadline: 'within_5_days',
    priority: 'critical',
    description: 'Submit complete loan application',
    compliance: 'Financing contingency'
  },
  {
    name: 'Financing Contingency',
    icon: faFileContract,
    trigger: 'upon_contract_acceptance',
    deadline: 'within_21_days',
    priority: 'critical',
    description: 'Secure loan approval',
    compliance: 'Contract protection'
  },
  {
    name: 'Title Report',
    icon: faFileContract,
    trigger: 'upon_contract_acceptance',
    deadline: 'within_5_days',
    priority: 'high',
    description: 'Order preliminary title report'
  },
  {
    name: 'Appraisal',
    icon: faHome,
    trigger: 'upon_contract_acceptance',
    deadline: 'within_14_days',
    priority: 'high',
    description: 'Complete property appraisal',
    compliance: 'Lender requirement'
  },
  {
    name: 'Seller Disclosures',
    icon: faExclamationTriangle,
    trigger: 'upon_listing',
    deadline: 'immediately',
    priority: 'critical',
    description: 'Provide all required disclosures',
    compliance: 'Legal requirement'
  },
  {
    name: 'HOA Documents',
    icon: faFileContract,
    trigger: 'upon_contract_acceptance',
    deadline: 'within_7_days',
    priority: 'high',
    description: 'Deliver HOA docs to buyer'
  },
  {
    name: 'Final Walk-Through',
    icon: faHome,
    trigger: 'prior_to_closing',
    deadline: 'within_24_hours',
    priority: 'medium',
    description: 'Buyer\'s final property inspection'
  }
];

interface DocumentTimingTemplatesProps {
  onSelectTemplate: (template: TimingTemplate) => void;
  documentType?: string;
}

const DocumentTimingTemplates: React.FC<DocumentTimingTemplatesProps> = ({ 
  onSelectTemplate,
  documentType 
}) => {
  // Suggest templates based on document type
  const getSuggestedTemplates = () => {
    if (!documentType) return TIMING_TEMPLATES;
    
    const docTypeLower = documentType.toLowerCase();
    if (docTypeLower.includes('earnest')) {
      return TIMING_TEMPLATES.filter(t => t.name.includes('Earnest'));
    }
    if (docTypeLower.includes('inspection')) {
      return TIMING_TEMPLATES.filter(t => t.name.includes('Inspection'));
    }
    if (docTypeLower.includes('disclosure')) {
      return TIMING_TEMPLATES.filter(t => t.name.includes('Disclosure'));
    }
    if (docTypeLower.includes('loan') || docTypeLower.includes('financing')) {
      return TIMING_TEMPLATES.filter(t => 
        t.name.includes('Loan') || t.name.includes('Financing')
      );
    }
    
    return TIMING_TEMPLATES;
  };

  const templates = getSuggestedTemplates();

  return (
    <div>
      <div className="mb-3 d-flex align-items-center">
        <FontAwesomeIcon icon={faLightbulb} className="me-2 text-warning" />
        <h6 className="mb-0">Common Timing Templates</h6>
      </div>
      
      <Row className="g-2">
        {templates.map((template, index) => (
          <Col md={6} lg={4} key={index}>
            <Card 
              className="h-100 cursor-pointer"
              style={{ 
                background: 'rgba(0, 212, 255, 0.05)',
                border: '1px solid rgba(0, 212, 255, 0.2)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => onSelectTemplate(template)}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 212, 255, 0.05)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Card.Body className="p-3">
                <div className="d-flex align-items-start mb-2">
                  <FontAwesomeIcon 
                    icon={template.icon} 
                    className="me-2 text-primary"
                    size="lg"
                  />
                  <div className="flex-grow-1">
                    <h6 className="mb-1">{template.name}</h6>
                    <p className="text-muted small mb-2">
                      {template.description}
                    </p>
                  </div>
                </div>
                
                <div className="mb-2">
                  <Badge 
                    bg={
                      template.priority === 'critical' ? 'danger' :
                      template.priority === 'high' ? 'warning' :
                      'info'
                    }
                    className="me-2"
                  >
                    {template.priority}
                  </Badge>
                  {template.compliance && (
                    <Badge bg="secondary" className="small">
                      {template.compliance}
                    </Badge>
                  )}
                </div>
                
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                  <FontAwesomeIcon icon={faClock} className="me-1" />
                  {template.deadline.replace(/_/g, ' ')}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default DocumentTimingTemplates;